/**
 * Apps2Go Hub – Ravintola-moduulin OMA henkilökuntahallinta (Phase 20)
 *
 * TÄYSIN ERILLINEN Apps2Go-pääkäyttäjähallinnasta. Käyttää omaa
 * `restaurant_staff`-kokoelmaa ja /api/restaurant-staff/* endpointtia.
 *
 *  - Lisäys: INLINE DIALOG (ei navigoi pois moduulista)
 *  - Roolit: Tarjoilija, Kokki, Kassa, Vuoropäällikkö, Hovimestari
 *  - PIN-koodi: 4-numeroinen, nopea tablet-kirjautuminen
 *  - Salasana: full email-login
 */
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Search, ShieldOff, UserPlus, Trash2, Pencil, KeyRound, Copy,
  ChefHat, Utensils, Banknote, Crown, User as UserIcon, ShieldCheck, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { key: "waiter",   label: "Tarjoilija",       icon: Utensils,   color: "from-amber-400 to-orange-500" },
  { key: "cook",     label: "Kokki",            icon: ChefHat,    color: "from-rose-400 to-red-600" },
  { key: "cashier",  label: "Kassa",            icon: Banknote,   color: "from-emerald-400 to-teal-600" },
  { key: "manager",  label: "Vuoropäällikkö",   icon: Crown,      color: "from-violet-400 to-purple-600" },
  { key: "host",     label: "Hovimestari",      icon: UserIcon,   color: "from-cyan-400 to-blue-600" },
];

const roleMeta = (key) => ROLES.find(r => r.key === key) || ROLES[0];

const emptyForm = () => ({
  name: "", email: "", password: "", role: "waiter", phone: "", pin: "", is_active: true,
});

export default function RestaurantStaffManager() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, mode: "create", data: emptyForm(), id: null });
  const [saving, setSaving] = useState(false);
  const [pinDialog, setPinDialog] = useState({ open: false, staff: null, pin: null });

  const isManager = user?.role === "admin" || user?.role === "license_customer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/restaurant-staff");
      setStaff(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isManager) load();
    else setLoading(false);
  }, [load, isManager]);

  const openCreate = () => setDialog({ open: true, mode: "create", data: emptyForm(), id: null });
  const openEdit = (s) => setDialog({
    open: true, mode: "edit",
    data: { name: s.name, email: s.email, password: "", role: s.role, phone: s.phone || "", pin: "", is_active: s.is_active },
    id: s.id,
  });

  const save = async () => {
    setSaving(true);
    try {
      const d = dialog.data;
      const payload = { ...d };
      if (dialog.mode === "edit" && !payload.password) delete payload.password;
      if (!payload.pin) delete payload.pin;
      if (dialog.mode === "create") {
        const r = await api.post("/restaurant-staff", payload);
        toast.success(`Henkilö "${r.data.name}" lisätty`);
      } else {
        const r = await api.put(`/restaurant-staff/${dialog.id}`, payload);
        toast.success(`Henkilö "${r.data.name}" päivitetty`);
      }
      setDialog({ open: false, mode: "create", data: emptyForm(), id: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Poistetaanko "${s.name}" ravintolan henkilökunnasta?`)) return;
    try {
      await api.delete(`/restaurant-staff/${s.id}`);
      toast.success("Henkilö poistettu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generatePin = async (s) => {
    try {
      const r = await api.post(`/restaurant-staff/${s.id}/generate-pin`);
      setPinDialog({ open: true, staff: s, pin: r.data.pin });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyPin = (pin) => {
    navigator.clipboard?.writeText(pin);
    toast.success(`PIN ${pin} kopioitu leikepöydälle`);
  };

  if (!isManager) {
    return (
      <div className="glass-card p-8 text-center" data-testid="restaurant-staff-no-access">
        <ShieldOff className="w-10 h-10 mx-auto text-white/30 mb-2" />
        <p className="text-sm text-white/50">Vain ylläpitäjä tai lisenssiomistaja saa hallita henkilökuntaa.</p>
      </div>
    );
  }

  const lower = search.trim().toLowerCase();
  const filtered = staff.filter(s => {
    if (filter !== "all" && s.role !== filter) return false;
    if (lower) {
      const hay = `${s.name} ${s.email} ${s.phone || ""}`.toLowerCase();
      if (!hay.includes(lower)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="restaurant-staff-manager">
      {/* Header */}
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-300/80">Ravintolan oma henkilökunta</p>
          <p className="text-sm text-white/70">
            {staff.length} henkilö{staff.length === 1 ? "" : "ä"} · Erillinen rekisteri – ei linkkiä Apps2Go-tunnuksiin
          </p>
        </div>
        <div className="flex gap-1" data-testid="restaurant-staff-filters">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1 rounded-full text-xs border ${filter === "all" ? "bg-amber-500/15 border-amber-400/40 text-amber-200" : "border-white/10 text-white/60"}`}
            data-testid="restaurant-staff-filter-all"
          >Kaikki</button>
          {ROLES.map(r => (
            <button
              key={r.key}
              onClick={() => setFilter(r.key)}
              className={`px-3 py-1 rounded-full text-xs border ${filter === r.key ? "bg-amber-500/15 border-amber-400/40 text-amber-200" : "border-white/10 text-white/60"}`}
              data-testid={`restaurant-staff-filter-${r.key}`}
            >{r.label}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Etsi…"
            className="pl-7 h-8 w-44 text-xs"
            data-testid="restaurant-staff-search"
          />
        </div>
        <Button
          onClick={openCreate}
          className="bg-gradient-to-r from-amber-500 to-orange-600 text-zinc-900 hover:from-amber-400 hover:to-orange-500 font-medium inline-flex items-center gap-1.5"
          data-testid="restaurant-staff-add-button"
        >
          <UserPlus className="w-4 h-4" /> Lisää henkilö
        </Button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-white/40 text-sm py-8 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Ladataan…
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="restaurant-staff-empty">
          <UserPlus className="w-10 h-10 mx-auto text-amber-500/30 mb-3" />
          <p className="text-white/50 mb-1">Ei henkilökuntaa</p>
          <p className="text-xs text-white/30 mb-4">Lisää ensimmäinen tarjoilija, kokki tai kassa.</p>
          <Button onClick={openCreate} className="bg-amber-500 text-zinc-900 hover:bg-amber-400" data-testid="restaurant-staff-empty-add">
            <UserPlus className="w-4 h-4 mr-1.5" /> Lisää ensimmäinen henkilö
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="restaurant-staff-grid">
          {filtered.map(s => {
            const m = roleMeta(s.role);
            const Icon = m.icon;
            return (
              <div
                key={s.id}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-amber-400/30 p-4 transition"
                data-testid={`restaurant-staff-card-${s.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{s.name}</p>
                    <p className="text-[10px] text-white/40 truncate">{s.email}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge className="bg-white/[0.04] text-white/70 text-[10px] border-white/10">{m.label}</Badge>
                      {!s.is_active && <Badge className="bg-red-500/15 text-red-300 text-[10px]">Pois käytöstä</Badge>}
                      {s.has_pin && <Badge className="bg-emerald-500/15 text-emerald-300 text-[10px]">PIN</Badge>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px]">
                  <span className="text-white/40">{s.phone || "—"}</span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => generatePin(s)} title="Generoi PIN" data-testid={`restaurant-staff-pin-${s.id}`}>
                      <KeyRound className="w-3.5 h-3.5 text-amber-300" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(s)} title="Muokkaa" data-testid={`restaurant-staff-edit-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(s)} title="Poista" data-testid={`restaurant-staff-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help banneri */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3 flex gap-2 items-start">
        <ShieldCheck className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-white/70">
          <strong className="text-emerald-200">Tärkeää:</strong> Tämä henkilökuntalista on TÄYSIN itsenäinen
          eikä se ole linkitetty Apps2Go-pääkäyttäjähallintaan. Henkilöt kirjautuvat
          osoitteessa <code className="text-amber-300">/restaurant/staff-login</code> joko
          sähköpostilla/salasanalla tai 4-numeroisella PIN-koodilla.
        </p>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ ...dialog, open: false })}>
        <DialogContent
          className="max-w-lg border-amber-500/20 bg-gradient-to-br from-zinc-900/95 via-zinc-950/95 to-zinc-900/95 backdrop-blur-2xl shadow-2xl shadow-amber-500/10"
          data-testid="restaurant-staff-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-amber-200">
              {dialog.mode === "create" ? "Lisää uusi henkilö" : "Muokkaa henkilöä"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-white/60">Nimi *</Label>
              <Input
                value={dialog.data.name}
                onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })}
                placeholder="Etunimi Sukunimi"
                data-testid="restaurant-staff-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-white/60">Sähköposti *</Label>
                <Input
                  type="email"
                  value={dialog.data.email}
                  onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, email: e.target.value } })}
                  placeholder="etunimi@ravintola.fi"
                  data-testid="restaurant-staff-form-email"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">Puhelin</Label>
                <Input
                  value={dialog.data.phone}
                  onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, phone: e.target.value } })}
                  placeholder="+358 40 1234567"
                  data-testid="restaurant-staff-form-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/60">Rooli *</Label>
              <div className="grid grid-cols-5 gap-1.5 mt-1">
                {ROLES.map(r => {
                  const Icon = r.icon;
                  const active = dialog.data.role === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setDialog({ ...dialog, data: { ...dialog.data, role: r.key } })}
                      className={`p-2 rounded-lg border flex flex-col items-center gap-1 text-[10px] transition ${active ? `bg-gradient-to-br ${r.color} border-transparent text-white` : "bg-white/[0.02] border-white/10 text-white/60 hover:bg-white/[0.05]"}`}
                      data-testid={`restaurant-staff-form-role-${r.key}`}
                    >
                      <Icon className="w-4 h-4" />
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-white/60">
                  Salasana {dialog.mode === "create" ? "*" : "(tyhjä = ei vaihdeta)"}
                </Label>
                <Input
                  type="password"
                  value={dialog.data.password}
                  onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, password: e.target.value } })}
                  placeholder="Vähintään 4 merkkiä"
                  data-testid="restaurant-staff-form-password"
                />
              </div>
              <div>
                <Label className="text-xs text-white/60">PIN-koodi (valinnainen)</Label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={dialog.data.pin}
                  onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, pin: e.target.value.replace(/\D/g, "").slice(0, 4) } })}
                  placeholder="4 numeroa"
                  data-testid="restaurant-staff-form-pin"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input
                type="checkbox"
                checked={dialog.data.is_active}
                onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, is_active: e.target.checked } })}
                data-testid="restaurant-staff-form-active"
              />
              Aktiivinen (voi kirjautua sisään)
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ ...dialog, open: false })} data-testid="restaurant-staff-form-cancel">Peruuta</Button>
            <Button
              onClick={save}
              disabled={saving || !dialog.data.name || !dialog.data.email || (dialog.mode === "create" && !dialog.data.password)}
              className="bg-amber-500 text-zinc-900 hover:bg-amber-400"
              data-testid="restaurant-staff-form-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tallenna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN-näytön dialogi */}
      <Dialog open={pinDialog.open} onOpenChange={(o) => !o && setPinDialog({ open: false, staff: null, pin: null })}>
        <DialogContent
          className="max-w-sm border-amber-500/20 bg-gradient-to-br from-zinc-900/95 via-zinc-950/95 to-zinc-900/95 backdrop-blur-2xl shadow-2xl shadow-amber-500/10"
          data-testid="restaurant-staff-pin-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-amber-200">Uusi PIN-koodi</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center space-y-3">
            <p className="text-sm text-white/70">{pinDialog.staff?.name}</p>
            <div className="text-5xl font-mono font-bold gold-text tracking-widest" data-testid="restaurant-staff-pin-value">
              {pinDialog.pin}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyPin(pinDialog.pin)}
              className="border-amber-400/30 text-amber-200"
              data-testid="restaurant-staff-pin-copy"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Kopioi
            </Button>
            <p className="text-[10px] text-white/40 mt-3">
              PIN tallennettiin järjestelmään hashittuna. Jaa se henkilölle nyt – et näe sitä enää uudelleen.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setPinDialog({ open: false, staff: null, pin: null })} data-testid="restaurant-staff-pin-close">Sulje</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
